import { LoadActions } from '@app/core/store/_prototypes/action-reducers/actions/load.actions';
import { LoadState } from '@app/core/store/_prototypes/action-reducers/state/load.state';
import { CoreState } from '@app/core/store/core.slice';
import { Store } from '@ngrx/store';
import { Observable, filter, map, takeUntil } from 'rxjs';
import { catchErrorsToStore } from '@app/shared/utils/pipe.functions';

export abstract class BaseSandbox {
  protected constructor(protected store: Store<CoreState>) {}

  /**
   * Create a generalized load pipeline.
   *
   * @param loadActions The load actions to dispatch to.
   * @param parameters The parameters to dispatch on loading.
   * @param loadPipeline The pipeline to execute on loading.
   */
  protected load<Key extends object, T>(
    loadActions: LoadActions<Key, T>,
    parameters: Key,
    loadPipeline: Observable<T>
  ) {
    this.store.dispatch(loadActions.load.execute({ request: parameters }));
    loadPipeline
      .pipe(catchErrorsToStore(this.store, loadActions.load.failure))
      .subscribe((data: T) => this.store.dispatch(loadActions.load.success({ data, loaded: parameters })));
  }

  /**
   * Creates a load pipeline which blocks calls where the parameters aren't the same anymore as the initial one.
   *
   * @param loadActions The load actions to dispatch to.
   * @param parameters The parameters to dispatch on loading.
   * @param loadPipeline The pipeline to execute on loading.
   * @param observableLoadState The {@link LoadState} to check.
   */
  protected loadOnlyLatest<Key extends object, T>(
    loadActions: LoadActions<Key, T>,
    parameters: Key,
    loadPipeline: Observable<T>,
    observableLoadState: Observable<LoadState<Key, any>>
  ) {
    this.store.dispatch(loadActions.load.execute({ request: parameters }));
    loadPipeline
      .pipe(
        takeUntil(
          observableLoadState.pipe(
            filter((loadState: LoadState<Key, any>) => loadState.loading),
            map((loadState: LoadState<Key, any>) => JSON.stringify(loadState.requested) !== JSON.stringify(parameters)),
            filter((changed: boolean) => changed)
          )
        ),
        catchErrorsToStore(this.store, loadActions.load.failure)
      )
      .subscribe((data: T) => this.store.dispatch(loadActions.load.success({ data, loaded: parameters })));
  }
}
